import { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import store, { AllState } from '../../../../store'
import { changeGanttParameter } from '../../../../store/project'
import {
  FitPeriod,
  GanttDisplayRangeMode,
  GanttParameterVO,
} from '../../../../domain/value-object/GanttParameterVO'
import { useProjectPrivateContext } from '../../../context/projectContext'
import { getFinancialStartMonth } from '../../../../utils/date'
import { ProjectPlanNewRow } from '../projectPlanNew'
import {
  PendingFitToken,
  planInitialGanttFit,
  resolvePendingFitToken,
} from './initialGanttFit'

export const useGanttFitPeriod = (
  rootProjectPlanUuid: string | undefined,
  rootProjectPlan: ProjectPlanNewRow | undefined
): FitPeriod | undefined => {
  const { project } = useProjectPrivateContext()
  return useMemo(() => {
    if (!rootProjectPlanUuid) {
      return GanttParameterVO.findFitPeriod([
        GanttParameterVO.periodOf(project.scheduledDate),
      ])
    }
    const cumulation = rootProjectPlan?.body?.cumulation
    return GanttParameterVO.findFitPeriod([
      {
        start: cumulation?.minScheduledStartDate,
        end: cumulation?.maxScheduledEndDate,
      },
      GanttParameterVO.periodOf(rootProjectPlan?.body?.wbsItem?.scheduledDate),
    ])
  }, [rootProjectPlanUuid, rootProjectPlan, project.scheduledDate])
}

/**
 * 記憶・復元で、保存値のないルートを開いたときに、合わせる期間に単位と表示期間を合わせてそのルートに保存する。
 * 保存値の有無はルートの表示設定の読み込み完了後に判定する。読み込み前に合わせると、保存済みの値を上書きするためである。
 */
export const useInitialGanttFit = ({
  projectUuid,
  rootProjectPlanUuid,
  isRootBodyLoaded,
  fitPeriod,
}: {
  projectUuid: string
  rootProjectPlanUuid: string | undefined
  isRootBodyLoaded: boolean
  fitPeriod: FitPeriod | undefined
}) => {
  const isRememberRange = useSelector(
    (state: AllState) =>
      state.project.displayRangeMode === GanttDisplayRangeMode.REMEMBER
  )
  const isProjectSaved = useSelector(
    (state: AllState) => state.project.isProjectGanttParameterSaved
  )
  const rootLoad = useSelector(
    (state: AllState) => state.project.rootGanttParameterLoad
  )
  const fiscalStartMonth = useSelector((state: AllState) =>
    getFinancialStartMonth(state.tenant.organization?.financialYearEndMonth)
  )
  const handledTokenRef = useRef<PendingFitToken>()

  const pendingToken = resolvePendingFitToken({
    isRememberRange,
    isProjectSaved,
    isRootBodyLoaded,
    rootProjectPlanUuid,
    rootLoad,
  })

  useEffect(() => {
    const plan = planInitialGanttFit({
      pendingToken,
      handledToken: handledTokenRef.current,
      fitPeriod,
      current:
        store.getState().project.ganttParameter ??
        GanttParameterVO.defaultValue(),
      fiscalStartMonth,
    })
    if (!plan) return
    handledTokenRef.current = plan.token
    if (!plan.parameter) return
    store.dispatch(
      changeGanttParameter(projectUuid, plan.parameter, rootProjectPlanUuid)
    )
  }, [
    pendingToken,
    fitPeriod,
    fiscalStartMonth,
    projectUuid,
    rootProjectPlanUuid,
  ])
}
